/**
 * PilotLeague - Professional Theme
 * 
 * Style: Sobre, professionnel, aviation
 * Cible: Pilotes sérieux, professionnels 30-50 ans
 * Inspiration: Navigraph, ForeFlight, SimBrief
 */

export const proTheme = {
  name: 'pro',
  label: 'Professional',
  
  // ============================================================================
  // ÉLÉMENTS COMMUNS (partagés dark/light)
  // ============================================================================
  common: {
    // Rayons de bordure (Pro: plus subtils - rounded-lg/xl)
    radius: {
      sm: 'rounded-md',
      md: 'rounded-lg',
      lg: 'rounded-xl',
      full: 'rounded-full',
    },
    // Transitions (Pro: minimales)
    transition: {
      fast: 'transition-all duration-150',
      normal: 'transition-all duration-200',
      slow: 'transition-all duration-300',
    },
    // Ombres (Pro: subtiles, bleu)
    shadow: {
      sm: 'shadow-sm',
      md: 'shadow',
      lg: 'shadow-md',
      glow: 'shadow-md shadow-blue-500/10',
    },
    // Typographie Pro: Semibold, formel
    typography: {
      heading: 'font-semibold',   // font-weight: 600
      body: 'font-normal',        // font-weight: 400
      label: 'font-medium',       // font-weight: 500
      tracking: 'tracking-wide',  // letter-spacing
      uppercase: false,           // Labels en minuscules
    },
  },

  // ============================================================================
  // MODE SOMBRE
  // ============================================================================
  dark: {
    name: 'dark',
    
    // Backgrounds
    bg: {
      primary: 'bg-slate-950',
      secondary: 'bg-slate-900',
      tertiary: 'bg-slate-800',
      card: 'bg-slate-900/80',
      cardHover: 'hover:bg-slate-800/60',
      cardAccent: 'bg-gradient-to-br from-blue-900/10 via-slate-900 to-slate-900',
      input: 'bg-slate-900/80',
      nav: 'bg-slate-950/95 backdrop-blur-2xl',
      modal: 'bg-slate-900',
      overlay: 'bg-black/70',
    },
    
    // Textes
    text: {
      primary: 'text-white',
      secondary: 'text-slate-300',
      tertiary: 'text-slate-400',
      muted: 'text-slate-500',
      inverse: 'text-slate-950',
      accent: 'text-blue-400',
      success: 'text-emerald-400',
      warning: 'text-amber-400',
      danger: 'text-red-400',
      info: 'text-sky-400',
    },
    
    // Bordures
    border: {
      primary: 'border-slate-800',
      secondary: 'border-slate-800/50',
      accent: 'border-blue-500/30',
      accentStrong: 'border-blue-500/50',
      success: 'border-emerald-500/30',
      danger: 'border-red-500/30',
    },
    
    // Accents & Gradients
    accent: {
      primary: 'bg-blue-500',
      primaryHover: 'hover:bg-blue-400',
      primaryText: 'text-blue-400',
      gradient: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      gradientText: 'bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent',
      subtle: 'bg-blue-500/10',
    },
    
    // États
    states: {
      success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
      danger: 'bg-red-500/10 border-red-500/30 text-red-400',
      info: 'bg-sky-500/10 border-sky-500/30 text-sky-400',
    },
    
    // Scores
    score: {
      excellent: 'text-emerald-400',
      good: 'text-blue-400',
      average: 'text-amber-400',
      poor: 'text-red-400',
    },
    
    // Couleurs HEX pour jauges SVG
    scoreHex: {
      excellent: '#10b981', // emerald-500 >= 85
      good: '#3b82f6',      // blue-500 >= 70
      average: '#f59e0b',   // amber-500 >= 50
      poor: '#ef4444',      // red-500 < 50
    },
    
    // Badges de niveau (Pro style - Gold/Silver/Bronze/Standard)
    rarity: {
      legendary: 'bg-amber-500/10 border-amber-500/40 text-amber-400',   // Gold
      epic: 'bg-slate-700 border-slate-500 text-slate-200',              // Silver
      rare: 'bg-amber-700/20 border-amber-600/40 text-amber-500',        // Bronze
      common: 'bg-slate-800 border-slate-700 text-slate-400',            // Standard
    },
  },

  // ============================================================================
  // MODE CLAIR
  // ============================================================================
  light: {
    name: 'light',
    
    // Backgrounds
    bg: {
      primary: 'bg-slate-50',
      secondary: 'bg-white',
      tertiary: 'bg-slate-100',
      card: 'bg-white',
      cardHover: 'hover:bg-slate-50',
      cardAccent: 'bg-gradient-to-br from-blue-50/50 via-white to-white',
      input: 'bg-white',
      nav: 'bg-white/95 backdrop-blur-2xl',
      modal: 'bg-white',
      overlay: 'bg-black/50',
    },
    
    // Textes
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-700',
      tertiary: 'text-slate-500',
      muted: 'text-slate-400',
      inverse: 'text-white',
      accent: 'text-blue-600',
      success: 'text-emerald-600',
      warning: 'text-amber-600',
      danger: 'text-red-600',
      info: 'text-sky-600',
    },
    
    // Bordures
    border: {
      primary: 'border-slate-200',
      secondary: 'border-slate-100',
      accent: 'border-blue-300',
      accentStrong: 'border-blue-400',
      success: 'border-emerald-300',
      danger: 'border-red-300',
    },
    
    // Accents & Gradients
    accent: {
      primary: 'bg-blue-600',
      primaryHover: 'hover:bg-blue-700',
      primaryText: 'text-blue-600',
      gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600',
      gradientText: 'bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent',
      subtle: 'bg-blue-50',
    },
    
    // États
    states: {
      success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      warning: 'bg-amber-50 border-amber-200 text-amber-700',
      danger: 'bg-red-50 border-red-200 text-red-700',
      info: 'bg-sky-50 border-sky-200 text-sky-700',
    },
    
    // Scores
    score: {
      excellent: 'text-emerald-600',
      good: 'text-blue-600',
      average: 'text-amber-600',
      poor: 'text-red-600',
    },
    
    // Couleurs HEX pour jauges SVG
    scoreHex: {
      excellent: '#059669', // emerald-600 >= 85
      good: '#2563eb',      // blue-600 >= 70
      average: '#d97706',   // amber-600 >= 50
      poor: '#dc2626',      // red-600 < 50
    },
    
    // Badges de niveau (Pro style - Gold/Silver/Bronze/Standard)
    rarity: {
      legendary: 'bg-amber-50 border-amber-300 text-amber-700',    // Gold
      epic: 'bg-slate-100 border-slate-300 text-slate-700',         // Silver
      rare: 'bg-amber-50 border-amber-200 text-amber-600',          // Bronze
      common: 'bg-slate-50 border-slate-200 text-slate-500',        // Standard
    },
  },

  // ============================================================================
  // CARACTÉRISTIQUES DU STYLE
  // ============================================================================
  features: {
    // Pas d'emojis
    useEmojis: false,
    
    // Style de langage
    language: 'formal',
    
    // Labels et textes
    labels: {
      score: 'Score',
      xp: 'Points',
      level: 'Level',
      rank: 'Rank',
      landing: {
        excellent: 'Excellent',
        good: 'Good',
        average: 'Average',
        poor: 'Needs Improvement',
      },
      achievements: {
        legendary: 'Gold',
        epic: 'Silver',
        rare: 'Bronze',
        common: 'Standard',
      },
      greetings: {
        morning: 'Good morning',
        afternoon: 'Good afternoon',
        evening: 'Good evening',
      },
    },
    
    // Animations (réduites)
    animations: {
      enabled: true,
      scoreReveal: false,
      xpGain: false,
      levelUp: false,
    },
    
    // Affichage discret
    showXP: false,
    showStreak: true,
    
    // Icônes (Lucide React, pas d'emojis)
    icons: {
      flight: 'Plane',
      landing: 'PlaneLanding',
      score: 'Target',
      trophy: 'Trophy',
      medal: 'Award',
      fire: 'Flame',
      star: 'Star',
      crown: 'Crown',
    },
  },
};

export default proTheme;
